import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames/bind';
import { findDOMNode } from 'react-dom';
import { get as _get } from 'lodash';

import isTouch from 'utils/isTouch';
import { optionalAttribute } from 'utils/jsx';

import { Icon } from 'sheets';

import styles from './Dropdown.css';

const cx = classNames.bind(styles);

class Dropdown extends React.Component {
  constructor() {
    super();
    this.state = {
      isOpen: false,
    };

    this.handleClick = this.handleClick.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.toggleOptions = this.toggleOptions.bind(this);
  }

  componentDidMount() {
    window.addEventListener(`click`, this.handleClick);

    if (isTouch()) {
      window.addEventListener(`touchend`, this.handleTouchEnd);
    }
  }

  componentWillUnmount() {
    window.removeEventListener(`click`, this.handleClick);

    if (isTouch()) {
      window.removeEventListener(`touchend`, this.handleTouchEnd);
    }
  }

  handleClick(event) {
    if (!findDOMNode(this.selectedOption).contains(event.target) && this.state.isOpen) {
      this.hideOptions();
    }
  }

  handleTouchEnd(event) {
    if (!findDOMNode(this.selectedOption).contains(event.target) && !findDOMNode(this.optionList).contains(event.target) && this.state.isOpen) {
      this.hideOptions();
    }
  }

  hideOptions() {
    this.setState({
      isOpen: false,
    });
  }

  toggleOptions() {
    if (!this.state.isOpen) {
      this.setState({
        isOpen: true,
      });

      return;
    }

    this.setState({
      isOpen: false,
    });
  }

  getSelectedOption() {
    const matchedOption = this.props.options.filter((option) => {
      return option.value === this.props.selectedValue;
    })[0];

    return matchedOption;
  }

  renderDimensions(option) {
    let dimensions = _get(option, `optional.dimensions`);

    if (!dimensions) {
      return null;
    }

    if (this.props.type === `tight`) {
      dimensions = dimensions.replace(/ /g, ``);
    }

    return (
      <span className={styles[`optional`]}>
        {dimensions}
      </span>
    );
  }

  renderOptional(optional) {
    if (!optional) {
      return null;
    }

    return Object.keys(optional).map((key) => {
      if (key === `readyToShip`) {
        return null;
      }

      return (
        <span
          className={cx(`optional`, `optional--${key}`)}
          key={key}
        >
          {optional[key]}
        </span>
      );
    });
  }

  renderListItems() {
    return this.props.options.map((option) => {
      const isSelected = option.value === this.props.selectedValue;
      const fn = () => this.props.onClick(option.value);

      const onClick = optionalAttribute(`onClick`, fn, this.props.onClick);
      const selectedOption = optionalAttribute(`selected`, isSelected, isSelected);

      return (
        <li
          className={styles[`option`]}
          key={option.value}
          {...onClick}
          {...selectedOption}
        >
          <span className={styles[`label`]}>
            {option.label}
          </span>
          {this.renderOptional(option.optional)}
        </li>
      );
    });
  }

  render() {
    const selectedOption = this.getSelectedOption();
    const containerClass = classNames(styles[`container`], {
      [styles[`container--open`]]: this.state.isOpen,
      [styles[`container--tight`]]: this.props.type === `tight`,
      [this.props.containerStyles]: this.props.containerStyles,
    });
    const buttonStyles = classNames(styles[`selected-option`], {
      [this.props.buttonStyles]: this.props.buttonStyles,
    });
    const arrowStyles = classNames(styles[`selected-option-arrow`], {
      [this.props.arrowStyles]: this.props.arrowStyles,
    });
    const optionsStyles = classNames(styles[`options`], {
      [this.props.optionsStyles]: this.props.optionsStyles,
    });

    return (
      <div className={containerClass}>
        <button
          className={buttonStyles}
          onClick={this.toggleOptions}
          ref={(el) => { this.selectedOption = el; }}
          type="button"
        >
          <span className={styles[`label`]}>
            {selectedOption.label}
          </span>
          {this.props.showSelectedOptional ? this.renderOptional(selectedOption.optional) : null}
          {this.renderDimensions(selectedOption)}
          <Icon
            className={arrowStyles}
            name="arrow"
          />
        </button>
        <ul
          className={optionsStyles}
          ref={(el) => { this.optionList = el; }}
        >
          {this.renderListItems()}
        </ul>
      </div>
    );
  }
}

Dropdown.propTypes = {
  arrowStyles: PropTypes.string,
  buttonStyles: PropTypes.string,
  containerStyles: PropTypes.string,
  onClick: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    optional: PropTypes.object,
  })).isRequired,
  optionsStyles: PropTypes.string,
  selectedValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  showSelectedOptional: PropTypes.bool,
  type: PropTypes.oneOf([`tight`]),
};

export default Dropdown;
